import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { XIcon, ChevronLeftIcon, ChevronRightIcon, ShoppingBagIcon, RulerIcon, MapPinIcon, RotateCcwIcon, TruckIcon } from 'lucide-react';
import { Apparel } from '../../data/retail';
import { money } from '../../data/food';
import { MerchantBadge } from '../MerchantBadge';
import { merchantFor } from '../../data/merchants';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../AuthContext';

/**
 * A garment's detail view is a fitting room, not a spec sheet: the colourway
 * changes the photograph, every size the boutique stocks is visible at once
 * with what is actually sold out, and the fit note sits next to the size grid
 * where the decision is made.
 */
export function ApparelDetailModal({
  id,
  title,
  location,
  apparel,
  initialColourId,
  initialSize,
  onClose
}: {
  id: string;
  title: string;
  location: string;
  apparel: Apparel;
  initialColourId?: string;
  initialSize?: string | null;
  onClose: () => void;
}) {
  const {
    requireAuth
  } = useAuth();
  const {
    add,
    setOpen
  } = useCart();
  const [colourId, setColourId] = useState(initialColourId ?? apparel.colours[0].id);
  const [size, setSize] = useState<string | null>(initialSize ?? null);
  const colour = apparel.colours.find(c => c.id === colourId) ?? apparel.colours[0];
  const index = apparel.colours.findIndex(c => c.id === colour.id);
  const store = location.split(' · ')[0];
  const inStock = apparel.sizes.filter(s => s.inStock).length;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  const step = (dir: 1 | -1) => setColourId(apparel.colours[(index + dir + apparel.colours.length) % apparel.colours.length].id);
  return createPortal(<div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6" data-id="element-3265" data-mp="2p5w6eyceofnk">
      <div className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" onClick={onClose} data-id="element-3266" data-mp="2ad2tnkoiqdml" />
      <motion.div initial={{
      opacity: 0,
      y: 24,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} role="dialog" aria-modal="true" aria-label={title} className="relative w-full sm:max-w-3xl bg-white rounded-t-3xl sm:rounded-3xl shadow-large overflow-hidden flex flex-col sm:flex-row max-h-[92vh] sm:max-h-[88vh] sm:h-[640px]" data-id="element-3267" data-mp="21e8pn98uykz0">
        <button onClick={onClose} aria-label="Close" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-slate-600 hover:text-dark-navy shadow-sm transition-colors duration-150 ease-out" data-id="element-3268" data-mp="3qsncfo0bfxrv">
          <XIcon className="w-4 h-4" data-id="element-3269" data-mp="1owxw0rr6wgww" />
        </button>

        {/* The garment, browsable by colourway */}
        <div className="relative flex-shrink-0 h-56 sm:h-auto sm:w-[45%] bg-slate-100 group" data-id="element-3270" data-mp="1k2tp28hxc8vt">
          <img src={colour.image} alt={`${title} in ${colour.label}`} className="w-full h-full object-cover" data-id="element-3271" data-mp="1xui2cf3wt3ed" />
          {apparel.colours.length > 1 && <>
              <button onClick={() => step(-1)} aria-label="Previous colour" className="absolute left-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center text-slate-700 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-150 ease-out" data-id="element-3272" data-mp="27s4tbzyhglep">
                <ChevronLeftIcon className="w-4 h-4" data-id="element-3273" data-mp="9y0penxzbgte" />
              </button>
              <button onClick={() => step(1)} aria-label="Next colour" className="absolute right-2.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center text-slate-700 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-150 ease-out" data-id="element-3274" data-mp="ewu1cbxmazrg">
                <ChevronRightIcon className="w-4 h-4" data-id="element-3275" data-mp="3nblxfvp1mce4" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-id="element-3276" data-mp="3sct4fsjd37jq">
                {apparel.colours.map((c, i) => <span key={c.id} className={`h-1 rounded-full transition-all duration-200 ease-out ${i === index ? 'w-4 bg-white' : 'w-1 bg-white/60'}`} data-id="element-3277" data-mp="5wozehb70tko" data-mpi={i} />)}
              </div>
            </>}
        </div>

        <div className="flex-1 flex flex-col min-w-0" data-id="element-3278" data-mp="1qqnq3haydqbg">
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5" data-id="element-3279" data-mp="1qqe0huttll6a">
            <div data-id="element-3280" data-mp="1mqdi5z3lopty">
              <MerchantBadge merchant={merchantFor(apparel.brand, 'Boutique')} showKind data-id="element-3281" data-mp="1mpu5awst4rms" />
              <h2 className="font-display font-bold text-xl text-dark-navy mt-3 leading-snug" data-id="element-3282" data-mp="1cr4tnlri83ez">
                {title}
              </h2>
              <p className="text-[13px] text-slate-500 mt-1" data-id="element-3283" data-mp="17t3az1xzq641">
                {apparel.material}
              </p>
              <div className="flex items-baseline gap-2 mt-2.5" data-id="element-3284" data-mp="12zs2kwdj5ha0">
                <span className="font-display font-bold text-xl text-dark-navy" data-id="element-3285" data-mp="yz86lw4p0jpg">
                  {money(apparel.price)}
                </span>
                {apparel.wasPrice && <>
                    <span className="text-[13px] text-slate-400 line-through" data-id="element-3286" data-mp="p1tqxyqlfyy7">
                      {money(apparel.wasPrice)}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-dark-navy text-white text-[11px] font-bold" data-id="element-3287" data-mp="k4tk3llx6s61">
                      {Math.round((1 - apparel.price / apparel.wasPrice) * 100)}
                      % off
                    </span>
                  </>}
              </div>
              <p className="inline-flex items-center gap-1.5 text-[12px] text-slate-400 mt-2" data-id="element-3288" data-mp="1gjeqidjx0yy">
                <MapPinIcon className="w-3.5 h-3.5" data-id="element-3289" data-mp="5hled0bdrf1g" />
                {location}
              </p>
            </div>

            {/* Colour by name, chosen by swatch */}
            <div data-id="element-3290" data-mp="y3xsu8vqgltt">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-3291" data-mp="y6jy21v5ip8v">
                Colour — {colour.label}
              </h3>
              <div className="flex items-center gap-2.5 mt-2.5" data-id="element-3292" data-mp="oduo5bf91jfn">
                {apparel.colours.map(c => <button key={c.id} onClick={() => setColourId(c.id)} aria-label={c.label} aria-pressed={c.id === colourId} className={`w-9 h-9 rounded-full border transition-[box-shadow,border-color] duration-150 ease-out ${c.id === colourId ? 'border-dark-navy ring-2 ring-dark-navy/15' : 'border-slate-200 hover:border-slate-400'}`} style={{
                backgroundColor: c.hex
              }} data-id="element-3293" data-mp="1c3f9g0yvkss3" />)}
              </div>
            </div>

            {/* Every size at once — this is where a fitting room beats a card */}
            <div data-id="element-3294" data-mp="2dogi51x0wfoo">
              <div className="flex items-baseline justify-between gap-3" data-id="element-3295" data-mp="2doydzxfrwrw2">
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400" data-id="element-3296" data-mp="2slpeka16o9n9">
                  Size
                </h3>
                <span className="text-[11px] font-semibold text-slate-400" data-id="element-3297" data-mp="2nnnw9rbl0ecu">
                  {inStock} of {apparel.sizes.length} in stock
                </span>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5 mt-2.5" data-id="element-3298" data-mp="2uu5tvv32ujg">
                {apparel.sizes.map(s => <button key={s.label} disabled={!s.inStock} onClick={() => setSize(s.label)} title={s.inStock ? undefined : 'Sold out'} className={`px-2 py-2.5 rounded-xl border text-[13px] font-bold transition-colors duration-150 ease-out ${!s.inStock ? 'border-slate-100 text-slate-300 line-through cursor-not-allowed' : size === s.label ? 'border-dark-navy bg-dark-navy text-white' : 'border-slate-200 text-slate-700 hover:border-slate-400'}`} data-id="element-3299" data-mp="1wbzypaiu0ct7">
                    {s.label}
                  </button>)}
              </div>
              <p className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-500 mt-3" data-id="element-3300" data-mp="ur240dghc338">
                <RulerIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3301" data-mp="s4ay7grkhwox" />
                {apparel.sizeNote ?? 'True to size'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2" data-id="element-3302" data-mp="nbzxibp066vb">
              <p className="flex items-start gap-2 px-3.5 py-3 bg-slate-50 rounded-xl text-[12px] text-slate-600 leading-snug" data-id="element-3303" data-mp="idyd9nfuc1i9">
                <TruckIcon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-3304" data-mp="dgpt5hb4tned" />
                Delivered in 2–3 days across the UAE
              </p>
              <p className="flex items-start gap-2 px-3.5 py-3 bg-slate-50 rounded-xl text-[12px] text-slate-600 leading-snug" data-id="element-3305" data-mp="1642e5n1n9n5k">
                <RotateCcwIcon className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" data-id="element-3306" data-mp="1146axq0rtzr2" />
                Free returns within 14 days
              </p>
            </div>
          </div>

          <div className="px-5 sm:px-6 py-4 border-t border-slate-100 flex-shrink-0" data-id="element-3307" data-mp="3lvpzel7apr8j">
            <button disabled={!size} onClick={() => requireAuth(() => {
            if (!size) return;
            add({
              listingId: id,
              merchant: store,
              title,
              image: colour.image,
              summary: `Size ${size} · ${colour.label}`,
              unitPrice: apparel.price,
              qty: 1
            });
            onClose();
            setOpen(true);
          })} className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-primary-indigo text-white text-sm font-bold rounded-2xl hover:bg-indigo-700 disabled:bg-slate-100 disabled:text-slate-400 transition-colors duration-150 ease-out" data-id="element-3308" data-mp="3gw3pakmrkr3y">
              <ShoppingBagIcon className="w-4 h-4" data-id="element-3309" data-mp="2hyu8nc39pdry" />
              {size ? `Add size ${size} · ${money(apparel.price)}` : 'Choose a size to continue'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>, document.body);
}